import { Box, Typography } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';

import type { VideoViewConfig } from '../../Types/AuthServer';

interface YouTubePlayerProps {
    showBackgroundForImages?: boolean;
    url: string;
    videoConfig?: VideoViewConfig;
}

/**
 * Enhanced YouTube Player component with comprehensive iframe parameters
 *
 * Supports:
 * - Individual YouTube videos (all URL formats)
 * - YouTube playlists
 * - All major YouTube iframe API parameters
 * - Optimized for autoplay in infoscreens
 * - Proper error handling and loading states
 *
 * Supported URL formats:
 * - https://www.youtube.com/watch?v=VIDEO_ID
 * - https://youtu.be/VIDEO_ID
 * - https://www.youtube.com/embed/VIDEO_ID
 * - https://www.youtube.com/v/VIDEO_ID
 * - https://www.youtube.com/shorts/VIDEO_ID
 * - https://www.youtube.com/playlist?list=PLAYLIST_ID
 * - https://www.youtube.com/watch?v=VIDEO_ID&list=PLAYLIST_ID
 */

// Extract YouTube video ID or playlist ID from URL
const extractYouTubeInfo = (url: string): { playlistId?: string; videoId?: string } | null => {
    if (!url) {
        return null;
    }

    // Check for playlist URLs first
    const playlistPatterns = [
        /(?:youtube\.com\/playlist\?list=|youtube\.com\/watch\?.*list=)([a-zA-Z0-9_-]+)/,
        /youtube\.com\/embed\/videoseries\?list=([a-zA-Z0-9_-]+)/,
    ];

    for (const pattern of playlistPatterns) {
        const match = pattern.exec(url);
        if (match?.[1]) {
            return { playlistId: match[1] };
        }
    }

    // Check for individual video URLs
    const videoPatterns = [
        /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
        /youtube\.com\/v\/([a-zA-Z0-9_-]{11})/,
        /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
    ];

    for (const pattern of videoPatterns) {
        const match = pattern.exec(url);
        if (match?.[1]?.length === 11) {
            return { videoId: match[1] };
        }
    }

    return null;
};

// Convert YouTube URL to embed URL with comprehensive parameters
const getYouTubeEmbedUrl = (url: string, videoConfig: VideoViewConfig | undefined): string => {
    const info = extractYouTubeInfo(url);
    if (!info) {
        return url;
    } // Return original URL if not YouTube

    // Extract configuration with defaults
    const showControls = videoConfig?.showControls ?? false;
    const muted = videoConfig?.muted ?? true;
    const autoLoop = videoConfig?.autoLoop ?? true;
    const quality = videoConfig?.quality ?? 'hd720';
    const autoplay = videoConfig?.autoplay ?? true;

    // Base parameters for all videos/playlists
    const baseParams = new URLSearchParams({
        autoplay: autoplay ? '1' : '0', // Start playing automatically
        controls: showControls ? '1' : '0', // Show or hide player controls
        disablekb: showControls ? '0' : '1', // Disable keyboard controls if hidden
        mute: muted ? '1' : '0', // Mute setting from config

        // Display and branding
        iv_load_policy: '3', // Hide video annotations
        modestbranding: '1', // Reduce YouTube branding
        rel: '0', // Don't show related videos at the end
        showinfo: '0', // Hide video info (deprecated but kept for compatibility)

        // Playback behavior
        enablejsapi: '1', // Enable JavaScript API
        fs: '1', // Allow fullscreen
        origin: window.location.origin, // Set origin for security

        // Additional display options
        color: 'white', // Progress bar color (red/white)
        playsinline: '1', // Play inline on mobile devices
        widget_referrer: window.location.href, // Referrer for analytics

        // Quality and performance
        cc_load_policy: '0', // Don't show closed captions by default
        hl: 'en', // Interface language
        vq: quality, // Preferred video quality from config

        // Advanced parameters - remove empty/default values to prevent cache issues
        start: '0', // Start time in seconds (can be customized)
        // Don't include 'end' parameter if empty - reduces URL complexity
        // Don't include 't' parameter as it's redundant with 'start'
    });

    // Remove empty parameters that might cause URL inconsistencies
    const cleanParams = new URLSearchParams();
    for (const [key, value] of baseParams) {
        if (value && value !== '' && value !== '0s') {
            cleanParams.append(key, value);
        }
    }

    if (info.playlistId) {
        // Handle playlist
        cleanParams.append('listType', 'playlist');
        cleanParams.append('list', info.playlistId);
        cleanParams.append('loop', autoLoop ? '1' : '0'); // Loop the entire playlist from config
        cleanParams.append('shuffle', '0'); // Don't shuffle playlist by default
        cleanParams.append('index', '1'); // Start with first video in playlist

        return `https://www.youtube.com/embed/videoseries?${cleanParams.toString()}`;
    } else if (info.videoId) {
        // Handle single video
        if (autoLoop) {
            cleanParams.append('loop', '1'); // Loop the video
            cleanParams.append('playlist', info.videoId); // Required for looping single video
        }

        return `https://www.youtube.com/embed/${info.videoId}?${cleanParams.toString()}`;
    }

    return url;
};

export default function YouTubePlayer({
    showBackgroundForImages = false,
    url,
    videoConfig,
}: Readonly<YouTubePlayerProps>) {
    // Memoize video processing to prevent unnecessary recalculations
    const youtubeInfo = useMemo(() => extractYouTubeInfo(url), [url]);
    const embedUrl = useMemo(() => getYouTubeEmbedUrl(url, videoConfig), [url, videoConfig]);

    // Create a stable key that only changes when the actual content changes
    // This prevents unnecessary iframe remounts and reduces network requests
    const iframeKey = useMemo(() => {
        if (!youtubeInfo) {
            return 'no-content';
        }
        const contentId = youtubeInfo.videoId || youtubeInfo.playlistId || 'unknown';
        const showControls = videoConfig?.showControls ?? false;
        return `youtube-${contentId}-${showControls ? 'controls' : 'no-controls'}`;
    }, [youtubeInfo, videoConfig?.showControls]);

    // Simple state management without complex effects
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);

    // Memoized event handlers to prevent unnecessary re-renders
    const handleLoad = useCallback(() => {
        setIsLoading(false);
        setHasError(false);
    }, []);

    const handleError = useCallback(() => {
        setIsLoading(false);
        setHasError(true);
    }, []);

    if (!url) {
        return (
            <Box
                sx={{
                    alignItems: 'center',
                    backgroundColor: showBackgroundForImages ? 'transparent' : '#000000',
                    display: 'flex',
                    height: '100%',
                    justifyContent: 'center',
                    width: '100%',
                }}
            >
                <Typography
                    sx={{
                        color: '#ffffff',
                        fontSize: '2rem',
                        textAlign: 'center',
                    }}
                    variant="h4"
                >
                    📹 No video URL provided
                </Typography>
            </Box>
        );
    }

    if (!youtubeInfo) {
        return (
            <Box
                sx={{
                    alignItems: 'center',
                    backgroundColor: showBackgroundForImages ? 'transparent' : '#000000',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    height: '100%',
                    justifyContent: 'center',
                    width: '100%',
                }}
            >
                <Typography
                    sx={{
                        color: '#ffffff',
                        fontSize: '2rem',
                        textAlign: 'center',
                    }}
                    variant="h4"
                >
                    ❌ Invalid YouTube URL
                </Typography>
                <Typography
                    sx={{
                        color: '#cccccc',
                        fontSize: '1rem',
                        textAlign: 'center',
                    }}
                >
                    Please provide a valid YouTube video or playlist URL
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                alignItems: 'center',
                backgroundColor: showBackgroundForImages ? 'transparent' : '#000000',
                display: 'flex',
                height: '100%',
                justifyContent: 'center',
                position: 'relative',
                width: '100%',
            }}
        >
            {isLoading ? (
                <Box
                    sx={{
                        alignItems: 'center',
                        backgroundColor: showBackgroundForImages ? 'transparent' : '#000000',
                        display: 'flex',
                        height: '100%',
                        justifyContent: 'center',
                        left: 0,
                        position: 'absolute',
                        top: 0,
                        width: '100%',
                        zIndex: 1,
                    }}
                >
                    <Typography
                        sx={{
                            color: '#ffffff',
                            fontSize: '1.5rem',
                            textAlign: 'center',
                        }}
                    >
                        📺 Loading YouTube content...
                    </Typography>
                </Box>
            ) : null}

            {hasError ? (
                <Box
                    sx={{
                        alignItems: 'center',
                        backgroundColor: showBackgroundForImages ? 'transparent' : '#000000',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        height: '100%',
                        justifyContent: 'center',
                        left: 0,
                        position: 'absolute',
                        top: 0,
                        width: '100%',
                        zIndex: 1,
                    }}
                >
                    <Typography
                        sx={{
                            color: '#ffffff',
                            fontSize: '2rem',
                            textAlign: 'center',
                        }}
                    >
                        ⚠️ YouTube content failed to load
                    </Typography>
                    <Typography
                        sx={{
                            color: '#cccccc',
                            fontSize: '1rem',
                            textAlign: 'center',
                        }}
                    >
                        The YouTube video or playlist could not be loaded
                    </Typography>
                </Box>
            ) : null}

            <Box
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                component="iframe"
                key={iframeKey}
                onError={handleError}
                onLoad={handleLoad}
                src={embedUrl}
                sx={{
                    border: 'none',
                    height: '100%',
                    objectFit: 'contain',
                    width: '100%',
                }}
                title={youtubeInfo.playlistId ? 'YouTube playlist player' : 'YouTube video player'}
            />
        </Box>
    );
}
